<script setup lang="ts">
/**
 * Concept A — grouped, collapsible, permission-driven sidebar navigation.
 *
 * Renders the tree from the menu store (HEAD_MENU filtered by the user's rights), nothing else:
 * one collapsible group per HEAD_MENU module. Leaves that have a native V2 route
 * are <RouterLink>s; the rest bridge to the classic app (see config/menu.ts).
 *
 * Used by both the desktop aside and the mobile drawer in App.vue. On mobile,
 * tapping any link emits `navigate` so the parent can close the drawer.
 */
import { computed, nextTick, onMounted, onBeforeUnmount, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useMenuStore } from "@/stores/menu";
import { moduleIcon, isNativeRoute, targetPath } from "@/config/menu";
import { ALL_KIND_MENUS } from "@/utils/invoiceKinds";
import type { MenuModule } from "@/api/menu";

const emit = defineEmits<{ (e: "navigate"): void }>();

const store = useMenuStore();
const route = useRoute();

const query = ref("");
const searchInput = ref<HTMLInputElement | null>(null);

/** Module ids the user has expanded. */
const expanded = ref<Set<string>>(new Set());

onMounted(async () => {
    await store.load();
    // Open the group that owns the current route so the active item is visible.
    const active = store.modules.find(
        (m) =>
            m.items.some((it) => targetPath(it.action) === route.path) ||
            (!m.items.length && targetPath(m.action) === route.path),
    );
    if (active) expanded.value = new Set([active.id]);
    refreshIcons();
    window.addEventListener("keydown", onKeydown);
});

onBeforeUnmount(() => window.removeEventListener("keydown", onKeydown));

/** Ctrl/Cmd-K focuses the filter box. */
function onKeydown(e: KeyboardEvent) {
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        searchInput.value?.focus();
    }
}

const normalizedQuery = computed(() => query.value.trim().toLowerCase());

/**
 * "Sam svoj računovodja" companies have one screen for all invoices and offers (switched inside it), so their
 * "Računi" group is a single link to it. The leaves stay in the user's rights: the screen offers a kind only
 * when the user holds its menu right.
 */
const modules = computed<MenuModule[]>(() => {
    if (!store.isSsr) return store.modules;
    return store.modules.map((m) =>
        m.id === "racuni" && m.items.some((it) => ALL_KIND_MENUS.includes(it.id))
            ? { ...m, items: [], action: "racuni_izdaniRacuni" }
            : m,
    );
});

/** Modules (and their items) filtered by the search box. */
const filteredModules = computed<MenuModule[]>(() => {
    const q = normalizedQuery.value;
    if (!q) return modules.value;

    const out: MenuModule[] = [];
    for (const m of modules.value) {
        const moduleHit = m.label.toLowerCase().includes(q);
        const items = m.items.filter((it) => it.label.toLowerCase().includes(q));
        if (moduleHit || items.length) {
            // When the module name itself matches, keep all its items; otherwise
            // narrow to the matching leaves.
            out.push({ ...m, items: moduleHit ? m.items : items });
        }
    }
    return out;
});

/** While searching, every shown group is forced open. */
function isOpen(moduleId: string): boolean {
    return normalizedQuery.value !== "" || expanded.value.has(moduleId);
}

function toggle(moduleId: string) {
    if (normalizedQuery.value !== "") return; // no manual collapse mid-search
    const next = new Set(expanded.value);
    next.has(moduleId) ? next.delete(moduleId) : next.add(moduleId);
    expanded.value = next;
}

function itemIsActive(action: string): boolean {
    return targetPath(action) === route.path;
}

function moduleHasActive(m: MenuModule): boolean {
    return m.items.some((it) => itemIsActive(it.action));
}

function onNavigate() {
    emit("navigate");
}

// Lucide swaps <i data-lucide> for inline SVG; re-run whenever the rendered set
// changes (load, filter, expand/collapse).
function refreshIcons() {
    nextTick(() => {
        const lucide = (window as any).lucide;
        if (lucide?.createIcons) lucide.createIcons();
    });
}
watch([filteredModules, expanded, () => store.loaded], refreshIcons, { deep: true });
</script>

<template>
    <div class="flex flex-col h-full">
        <!-- Search / command box -->
        <div class="px-3 pt-3">
            <div class="flex items-center gap-2 bg-white/[0.07] rounded-lg px-2.5 py-2">
                <i data-lucide="search" class="w-4 h-4 text-white/45 flex-shrink-0"></i>
                <input
                    ref="searchInput"
                    v-model="query"
                    type="text"
                    placeholder="Išči  (Ctrl K)"
                    class="bg-transparent border-0 outline-none text-sm text-white placeholder-white/40 w-full"
                    aria-label="Išči po meniju"
                />
                <button
                    v-if="query"
                    @click="query = ''"
                    class="text-white/40 hover:text-white flex-shrink-0"
                    aria-label="Počisti iskanje"
                >
                    <i data-lucide="x" class="w-4 h-4"></i>
                </button>
            </div>
        </div>

        <nav class="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
            <!-- Loading / error / empty states -->
            <p v-if="store.loading" class="px-3 py-4 text-sm text-white/50">Nalagam meni…</p>
            <p v-else-if="store.error" class="px-3 py-4 text-sm text-red-300">{{ store.error }}</p>
            <p
                v-else-if="!filteredModules.length"
                class="px-3 py-4 text-sm text-white/50"
            >
                {{ query ? "Ni zadetkov." : "Ni menijev za to podjetje." }}
            </p>

            <!-- Module groups -->
            <div v-for="m in filteredModules" :key="m.id">
                <!-- Childless module: the heading itself navigates. -->
                <RouterLink
                    v-if="!m.items.length"
                    :to="targetPath(m.action)"
                    @click="onNavigate"
                    class="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold text-white/80 hover:text-white hover:bg-white/5 transition-colors"
                    active-class="bg-white/10 text-white"
                    :title="isNativeRoute(m.action) ? m.label : `Klasični zaslon: ${m.label}`"
                >
                    <i :data-lucide="moduleIcon(m.id)" class="w-4 h-4 flex-shrink-0"></i>
                    <span class="truncate flex-1">{{ m.label }}</span>
                    <i v-if="!isNativeRoute(m.action)" data-lucide="app-window" class="w-3.5 h-3.5 text-white/20 flex-shrink-0"></i>
                </RouterLink>

                <!-- Module with children: collapsible group. -->
                <button
                    v-else
                    type="button"
                    @click="toggle(m.id)"
                    class="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors"
                    :class="[
                        moduleHasActive(m) ? 'text-white' : 'text-white/80 hover:text-white hover:bg-white/5',
                    ]"
                    :aria-expanded="isOpen(m.id)"
                >
                    <i :data-lucide="moduleIcon(m.id)" class="w-4 h-4 flex-shrink-0"></i>
                    <span class="truncate text-left flex-1">{{ m.label }}</span>
                    <i
                        data-lucide="chevron-right"
                        class="w-4 h-4 text-white/40 flex-shrink-0 transition-transform"
                        :class="{ 'rotate-90': isOpen(m.id) }"
                    ></i>
                </button>

                <div v-if="m.items.length" v-show="isOpen(m.id)" class="mt-0.5 mb-1 space-y-0.5">
                    <RouterLink
                        v-for="it in m.items"
                        :key="it.id"
                        :to="targetPath(it.action)"
                        @click="onNavigate"
                        class="flex items-center gap-2 pl-11 pr-3 py-2 rounded-lg text-[13px] text-white/60 hover:text-white hover:bg-white/5 border-l-2 border-transparent ml-3.5 transition-colors"
                        :class="{ 'text-white bg-brand-blue/15 !border-brand-blue': itemIsActive(it.action) }"
                        :title="isNativeRoute(it.action) ? it.label : `Klasični zaslon: ${it.label}`"
                    >
                        <span class="truncate flex-1">{{ it.label }}</span>
                        <i
                            v-if="!isNativeRoute(it.action)"
                            data-lucide="app-window"
                            class="w-3 h-3 text-white/20 flex-shrink-0"
                        ></i>
                    </RouterLink>
                </div>
            </div>
        </nav>
    </div>
</template>
